/**
 * The user's short-lived Google access token for Drive metadata (visibility B, #788 §4).
 * IN MEMORY ONLY: a module variable, never localStorage, never logged. It is lost on reload and
 * asked for again; Google's consent screen shows once, after that the request is silent.
 */
const SCOPE = 'https://www.googleapis.com/auth/drive.metadata.readonly';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface TokenResponse {
  readonly access_token?: string;
  readonly error?: string;
}
interface TokenClient {
  requestAccessToken: (options?: { prompt?: string }) => void;
}
interface Gis {
  readonly accounts: {
    readonly oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: TokenResponse) => void;
        error_callback: (error: unknown) => void;
      }) => TokenClient;
    };
  };
}

let token: string | undefined;
let loading: Promise<boolean> | undefined;

export const getDriveToken = (): string | undefined => token;
export const clearDriveToken = (): void => {
  token = undefined;
};

/** The OAuth client id, from the environment; empty means «not configured». */
export const driveClientId = (): string =>
  String(import.meta.env.VITE_ARCHIVE_OAUTH_CLIENT_ID ?? '').trim();

const gis = (): Gis | undefined => (globalThis as { google?: Gis }).google;

/** Loads Google Identity Services once, and only when this screen needs it. */
function loadGis(): Promise<boolean> {
  if (gis()?.accounts?.oauth2) return Promise.resolve(true);
  loading ??= new Promise<boolean>((resolve) => {
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      loading = undefined;
      script.remove();
      resolve(false);
    };
    document.head.appendChild(script);
  });
  return loading;
}

export type ConsentResult = 'granted' | 'denied' | 'unconfigured' | 'failed';

/** Runs the token client; must be called from the click of «Consenti l'accesso». */
export async function requestDriveToken(): Promise<ConsentResult> {
  const clientId = driveClientId();
  if (clientId === '') return 'unconfigured';
  if (!(await loadGis())) return 'failed';
  const oauth2 = gis()?.accounts.oauth2;
  if (oauth2 === undefined) return 'failed';
  return new Promise<ConsentResult>((resolve) => {
    const client = oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      callback: (response) => {
        if (response.access_token === undefined || response.access_token === '') {
          resolve(response.error === 'access_denied' ? 'denied' : 'failed');
          return;
        }
        token = response.access_token;
        resolve('granted');
      },
      // The popup was closed or blocked: the person did not give the access.
      error_callback: () => resolve('denied'),
    });
    client.requestAccessToken({ prompt: '' });
  });
}
